import {TASK_NEW_STATUS_UPDATE_DELAY_MS, TaskForUITypes, TaskStatus, TaskSubtaskType} from './TasksStore.constants';
import {
  Task,
  TaskForUI,
  TaskForUIActive,
  TaskForUIBlocked,
  TaskForUIDone,
  TaskForUIProgress,
  TaskReward,
  TaskSubtask,
  TasksForUI,
  TasksState,
} from './TasksStore.types';
import {DI_TOKENS} from '@/di/di.tokens';

import {injected} from 'brandi';

import {Store} from '@/stores/Store';
import {GlobalWindow} from '@/types';

export class TasksStore extends Store<TasksState> {
  private doneTasksIdsForStartShowAnimation: Set<string> = new Set<string>();
  private progressTasksIdsForStartShowAnimation: Set<string> = new Set<string>();

  constructor(private readonly globalWindow: GlobalWindow) {
    super({
      tasks: [],
      dictTasksById: {},
      nextAvailableTaskDate: null,
      seenTasksIds: [],
      enabledMocks: false,
    });
  }

  public setTasks = ({tasks, isNeedTaskSort}: {tasks: Task[]; isNeedTaskSort?: boolean}): void => {
    this.setValue('tasks', isNeedTaskSort ? this.sortTasks(tasks) : tasks);
    this.setValue('dictTasksById', this.getDictTasksById(tasks));
  };

  private getDictTasksById = (tasks: Task[]): Record<string, Task> => {
    return tasks.reduce<Record<string, Task>>((acc, task) => {
      acc[task.taskId] = task;
      return acc;
    }, {});
  };

  public setNextAvailableTaskDate = (nextAvailableTaskDate: string | null): void => {
    this.setValue('nextAvailableTaskDate', nextAvailableTaskDate);
  };

  public setEnabledMocks = (enabledMocks: boolean): void => {
    this.setValue('enabledMocks', enabledMocks);
  };

  public static isTaskTypeDone = (task: TaskForUI): task is TaskForUIDone => {
    return task.type === TaskForUITypes.Done;
  };

  public static isTaskTypeActive = (task: TaskForUI): task is TaskForUIActive => {
    return task.type === TaskForUITypes.Active;
  };

  public static isTaskTypeBlocked = (task: TaskForUI): task is TaskForUIBlocked => {
    return task.type === TaskForUITypes.Blocked;
  };

  private static isSubtaskCanClaimReward = (subtask: TaskSubtask): boolean => {
    return subtask.canClaimReward && subtask.status === TaskStatus.InProgress;
  };

  private static isSubtaskInProgress = (subtask: TaskSubtask): boolean => {
    return subtask.status === TaskStatus.InProgress && !subtask.canClaimReward;
  };

  public isEnabledMocks = (): boolean => {
    return this.getState().enabledMocks;
  };

  public getTasks = (): Task[] => {
    return this.getState().tasks;
  };

  public getTasksForUI = (): TasksForUI => {
    const tasks = this.getState().tasks;

    return tasks.reduce<TasksForUI>(
      (acc, task) => {
        if (task.status === TaskStatus.InProgress) {
          acc.progress.push(this.buildTaskInProgressForUI(task));
        } else {
          acc.done.push(this.buildTaskDoneForUI(task));
        }

        return acc;
      },
      {progress: [], done: []},
    );
  };

  private buildTaskInProgressForUI = (task: Task): TaskForUIProgress => {
    const isCombo = task.subtasks.length > 1;
    const isBlocked = task.isBlocked || task.isExpired;

    const title = isCombo ? task.title : task.subtasks[0].title;
    const description = isCombo ? task.description : task.subtasks[0].description;
    const reward = isCombo ? task.reward : task.subtasks[0].reward;

    const baseProps = {
      id: task.taskId,
      title,
      wasSeen: task.wasSeen,
      reward,
    };

    if (isBlocked) {
      return {
        ...baseProps,
        type: TaskForUITypes.Blocked,
        executionRules: task.executionRules,
        isExpired: task.isExpired,
        isBlocked: task.isBlocked,
      };
    }

    const cta = task.subtasks.find((t) => t.cta && TasksStore.isSubtaskInProgress(t))?.cta || null;
    const showShareCta = !!task.subtasks.find(
      (t) => t.type === TaskSubtaskType.FriendInvitation && TasksStore.isSubtaskInProgress(t),
    );
    const isClickLink = !!task.subtasks.find(
      (t) => t.type === TaskSubtaskType.ClickLink && TasksStore.isSubtaskInProgress(t),
    );
    const canClaimReward =
      // Проверка для простого задания, либо отдельных сабтасков
      task.subtasks.some((t) => t.canClaimReward && t.status === TaskStatus.InProgress) ||
      // Проверка для комбо-задания, когда за сабтаски награды получены и можно забрать основную награду
      task.subtasks.every((t) => t.status === TaskStatus.Done);

    const isShowSubtasks = task.subtasks.length > 1;

    return {
      ...baseProps,
      type: TaskForUITypes.Active,
      description,
      withDelay: task.subtasks.some((t) => t.withDelay),
      canClaimReward,
      cta,
      showShareCta,
      isClickLink,
      subtasks: isShowSubtasks
        ? task.subtasks.map((t) => ({
            ...t,
            isDone: t.status === TaskStatus.Done,
            canClaimReward: t.canClaimReward && t.status === TaskStatus.InProgress,
            reward: t.status === TaskStatus.Done ? null : t.reward,
          }))
        : null,
    };
  };

  private buildTaskDoneForUI = (task: Task): TaskForUIDone => {
    const isCombo = task.subtasks.length > 1;

    return {
      type: TaskForUITypes.Done,
      id: task.taskId,
      title: isCombo ? task.title : task.subtasks[0].title,
      reward: isCombo ? task.reward : task.subtasks[0].reward,
      completedAt: task.completedAt,
    };
  };

  public getNextAvailableTaskDate = (): string | null => {
    return this.getState().nextAvailableTaskDate;
  };

  public getSeenTasksIds = (): string[] => {
    return this.getState().seenTasksIds;
  };

  public hasDoneTasksIdsForStartShowAnimation = (taskId: string): boolean => {
    return this.doneTasksIdsForStartShowAnimation.has(taskId);
  };

  public deleteIdFromDoneTasksIdsForStartShowAnimation = (taskId: string): void => {
    this.doneTasksIdsForStartShowAnimation.delete(taskId);
  };

  public hasProgressTasksIdsForStartShowAnimation = (taskId: string): boolean => {
    return this.progressTasksIdsForStartShowAnimation.has(taskId);
  };

  public deleteIdFromProgressTasksIdsForStartShowAnimation = (taskId: string): void => {
    this.progressTasksIdsForStartShowAnimation.delete(taskId);
  };

  public onNewTaskViewIntersection = (taskId: string): void => {
    const {tasks, seenTasksIds} = this.getState();
    const task = tasks.find((t) => t.taskId === taskId);

    if (task) {
      this.setValue('seenTasksIds', [...seenTasksIds, taskId]);
      this.globalWindow.setTimeout(() => {
        this.updateTaskWasSeen(taskId);
      }, TASK_NEW_STATUS_UPDATE_DELAY_MS);
    }
  };

  private updateTaskWasSeen = (taskId: string): void => {
    const {tasks} = this.getState();

    const updatedTasks = tasks.map((task) => {
      if (task.taskId === taskId) {
        return {
          ...task,
          wasSeen: true,
        };
      }
      return task;
    });

    this.setTasks({tasks: updatedTasks});
  };

  public clearSeenTasksIds = (): void => {
    this.setValue('seenTasksIds', []);
  };

  public hasNewTasks = (): boolean => {
    return this.getState().tasks.some((task) => !task.wasSeen && task.status === TaskStatus.InProgress);
  };

  public getTaskTitleById = (id: string): string => {
    const task = this.getState().tasks.find((t) => t.taskId === id);

    if (task) {
      return this.buildTaskInProgressForUI(task).title;
    }

    return '';
  };

  // Получение массива id сабтасков, у которых можно забрать награду
  public getSubtaskIdsForClaimReward = (taskId: string): number[] => {
    const task = this.getState().dictTasksById[taskId];

    return task.subtasks.reduce<number[]>((acc, subtask) => {
      if (TasksStore.isSubtaskCanClaimReward(subtask)) {
        acc.push(subtask.id);
      }
      return acc;
    }, []);
  };

  public getInProgressSubtaskId = (taskId: string): number | null => {
    const task = this.getState().dictTasksById[taskId];

    return task.subtasks.find((subtask) => TasksStore.isSubtaskInProgress(subtask))?.id || null;
  };

  // Получение общей награды за выполненые сабтаски/таски
  public getTotalRewardAfterClaimRewards = (taskId: string, subtaskIds: number[]): TaskReward => {
    const task = this.getState().dictTasksById[taskId];
    const resultReward: TaskReward = {
      energyRecharge: 0,
      multiTap: 0,
    };

    task.subtasks.forEach((t) => {
      if (subtaskIds.includes(t.id)) {
        resultReward.energyRecharge += t.reward.energyRecharge;
        resultReward.multiTap += t.reward.multiTap;
      }
    });

    if (task.status === TaskStatus.Done && task.subtasks.length > 1) {
      resultReward.energyRecharge += task.reward.energyRecharge;
      resultReward.multiTap += task.reward.multiTap;
    }

    return resultReward;
  };

  // Обновление статуса таски/сабтасков после получение награды
  public updateTasksStatusAfterClaimReward = (taskId: string, subtaskIds: number[]): void => {
    let newStatus: TaskStatus | null = null;

    const updatedTasks = this.getState().tasks.map((task) => {
      if (task.taskId === taskId) {
        const updatedSubtasks = task.subtasks.map((subtask) => {
          if (subtaskIds.includes(subtask.id)) {
            return {
              ...subtask,
              status: TaskStatus.Done,
            };
          }
          return subtask;
        });

        const isTaskDone = updatedSubtasks.every((t) => t.status === TaskStatus.Done);
        newStatus = isTaskDone ? TaskStatus.Done : task.status;

        return {
          ...task,
          status: newStatus,
          completedAt: isTaskDone ? new Date().toISOString() : task.completedAt,
          subtasks: updatedSubtasks,
        };
      }

      return task;
    });

    this.setTasks({tasks: this.sortTasksByCanClaimReward(updatedTasks)});

    if (newStatus === TaskStatus.InProgress) {
      this.progressTasksIdsForStartShowAnimation.add(taskId);
    }

    if (newStatus === TaskStatus.Done) {
      this.doneTasksIdsForStartShowAnimation.add(taskId);
    }
  };

  private sortTasks = (tasks: Task[]): Task[] => {
    const canClaimRewardTaskList: Task[] = [];
    const newTaskList: Task[] = [];
    const inProgressTaskList: Task[] = [];
    const blockedTaskList: Task[] = [];
    const doneTaskList: Task[] = [];

    for (const task of tasks) {
      if (task.subtasks.some((subtask) => TasksStore.isSubtaskCanClaimReward(subtask))) {
        canClaimRewardTaskList.push(task);
        continue;
      }
      if (task.isBlocked || task.isExpired) {
        blockedTaskList.push(task);
        continue;
      }
      if (!task.wasSeen) {
        newTaskList.push(task);
        continue;
      }
      if (task.status === TaskStatus.InProgress) {
        inProgressTaskList.push(task);
        continue;
      }
      if (task.status === TaskStatus.Done) {
        doneTaskList.push(task);
        continue;
      }
    }

    return [
      ...canClaimRewardTaskList,
      ...newTaskList,
      ...inProgressTaskList,
      ...blockedTaskList,
      ...this.sortTasksByDate(doneTaskList),
    ];
  };

  private sortTasksByCanClaimReward = (tasks: Task[]): Task[] => {
    const canClaimRewardTaskList: Task[] = [];
    const otherTaskList: Task[] = [];

    for (const task of tasks) {
      if (task.subtasks.some((subtask) => TasksStore.isSubtaskCanClaimReward(subtask))) {
        canClaimRewardTaskList.push(task);
        continue;
      }
      otherTaskList.push(task);
    }

    return [...canClaimRewardTaskList, ...otherTaskList];
  };

  private sortTasksByDate = (tasks: Task[]): Task[] => {
    return tasks.sort((a, b) => {
      if (!a.completedAt && !b.completedAt) {
        return 0;
      }

      if (!a.completedAt) {
        return -1;
      }

      if (!b.completedAt) {
        return 1;
      }

      return new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime();
    });
  };
}

injected(TasksStore, DI_TOKENS.globalWindow);
